import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReportVisibilityContext = createContext({
  visibility: {},
  pendingVisibility: {},
  isAdmin: false,
  loading: true,
  hasUnsavedChanges: false,
  toggleVisibility: () => {},
  saveVisibility: () => {},
  discardChanges: () => {},
  resetToDefaults: () => {},
  hiddenSectionsList: [],
});

export const useReportVisibility = () => useContext(ReportVisibilityContext);

// SECURITY: Admin email constant - single source of truth for frontend
const ADMIN_EMAIL = 'totalproductionsolutions@gmail.com';

export const ReportVisibilityProvider = ({ children }) => {
  const { user } = useAuth();
  const [visibility, setVisibility] = useState({});
  const [pendingVisibility, setPendingVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  // SECURITY: Only trust is_admin from backend, OR exact email match as fallback
  const isAdmin = user?.is_admin === true || user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const fetchVisibility = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/report-visibility`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const vis = res.data.visibility || {};
      setVisibility(vis);
      setPendingVisibility(vis);
    } catch (err) {
      console.error('Failed to fetch visibility settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchVisibility();
  }, [user, fetchVisibility]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!isAdmin) return false;
    const visKeys = Object.keys(visibility);
    const pendingKeys = Object.keys(pendingVisibility);
    const allKeys = new Set([...visKeys, ...pendingKeys]);
    for (const key of allKeys) {
      if (visibility[key] !== pendingVisibility[key]) {
        return true;
      }
    }
    return false;
  }, [visibility, pendingVisibility, isAdmin]);

  // Toggle visibility (only updates pending state, not saved)
  const toggleVisibility = useCallback((key) => {
    setPendingVisibility(prev => ({
      ...prev,
      [key]: prev[key] === false ? true : (prev[key] === true ? false : true)
    }));
  }, []);

  // Save all pending changes to backend
  const saveVisibility = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/admin/report-visibility`,
        { visibility: pendingVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVisibility(pendingVisibility);
      toast.success('Visibility settings saved');
    } catch (err) {
      console.error('Failed to save visibility:', err);
      toast.error('Failed to save visibility settings');
    }
  }, [pendingVisibility]);

  // Discard pending changes and revert to saved state
  const discardChanges = useCallback(() => {
    setPendingVisibility(visibility);
    toast.info('Changes discarded');
  }, [visibility]);

  // Reset all settings to defaults
  const resetToDefaults = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/report-visibility/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const defaultVis = res.data.visibility || {};
      setVisibility(defaultVis);
      setPendingVisibility(defaultVis);
      toast.success('Visibility settings reset to defaults');
    } catch (err) {
      console.error('Failed to reset visibility:', err);
      toast.error('Failed to reset visibility settings');
    }
  }, []);

  // Compute list of currently hidden sections (from saved visibility)
  const hiddenSectionsList = useMemo(() => {
    const hidden = [];
    for (const [key, value] of Object.entries(visibility)) {
      if (value === false) {
        hidden.push(key);
      }
    }
    return hidden;
  }, [visibility]);

  return (
    <ReportVisibilityContext.Provider value={{
      // For regular users: use saved visibility
      // For admins editing: use pendingVisibility so they see live preview
      visibility: isAdmin ? pendingVisibility : visibility,
      pendingVisibility,
      isAdmin,
      loading,
      hasUnsavedChanges,
      toggleVisibility,
      saveVisibility,
      discardChanges,
      resetToDefaults,
      hiddenSectionsList,
    }}>
      {children}
    </ReportVisibilityContext.Provider>
  );
};
